import type { EditorCommands } from "@linempo/react-state";
import { errorMessage } from "../../editor-geometry.js";

type StartupCommands = Pick<EditorCommands, "loadProject" | "newProject">;
export interface StartupWorkspace { readonly hasProject: boolean; readonly switching: boolean; readonly disposed: boolean }
export interface ProjectStartupState { readonly phase: "idle" | "loading" | "ready" | "failed"; readonly error: string | null }

/** A single startup attempt per session; React owns only its attachment/subscription.
 * A detached or superseded load must never create a late empty project.
 */
export function createProjectStartup(commands: StartupCommands, recentId: string) {
  let state: ProjectStartupState = { phase: "idle", error: null };
  let sequence = 0;
  let pending: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const owners = new Set<() => StartupWorkspace>();
  const owner = () => [...owners].at(-1)?.();
  const publish = (next: ProjectStartupState) => { state = next; for (const listener of listeners) listener(); };
  const start = (mode: "restore" | "new"): Promise<void> => {
    if (pending) return pending;
    const workspace = owner();
    if (!workspace || workspace.disposed || workspace.switching) return Promise.resolve();
    const attempt = ++sequence;
    const retainedError = state.error;
    publish({ phase: "loading", error: retainedError });
    const current = () => sequence === attempt && owners.size > 0 && owner()?.disposed === false;
    // Defer dispatch until the attachment is fully registered. StrictMode replay
    // can reuse this attempt before its first native operation starts.
    pending = Promise.resolve().then(async () => {
      if (!current()) return;
      if (mode === "restore" && recentId) {
        const restored = await commands.loadProject(recentId);
        if (!current()) return;
        if (restored) { publish({ phase: "ready", error: null }); return; }
      }
      if (!current()) return;
      await commands.newProject("未命名故事");
      if (current()) publish({ phase: "ready", error: null });
    }).catch((error: unknown) => {
      if (current()) publish({ phase: "failed", error: errorMessage(error) });
    }).finally(() => {
      if (sequence !== attempt) return;
      pending = null;
      if (!owners.size && state.phase === "loading") publish({ phase: "idle", error: null });
    });
    return pending;
  };
  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    attach: (getWorkspace: () => StartupWorkspace) => {
      owners.add(getWorkspace);
      const workspace = getWorkspace();
      if (state.phase === "idle" && !workspace.disposed && !workspace.switching) {
        if (workspace.hasProject) publish({ phase: "ready", error: null });
        else void start("restore");
      }
      return () => { owners.delete(getWorkspace); };
    },
    retry: () => start("restore"),
    create: () => start("new"),
    /** User-requested switching owns the session from this point forward. */
    supersede: () => { ++sequence; pending = null; publish({ phase: "ready", error: null }); },
  };
}

export type ProjectStartup = ReturnType<typeof createProjectStartup>;
const startups = new WeakMap<EditorCommands, ProjectStartup>();
export function projectStartupFor(commands: EditorCommands, recentId: string): ProjectStartup {
  let startup = startups.get(commands);
  if (!startup) { startup = createProjectStartup(commands, recentId); startups.set(commands, startup); }
  return startup;
}
